// ValueInput.jsx — renders the right input widget based on field type + op arity
import React from 'react';
import { useSelector } from 'react-redux';
import { Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import Select from 'react-select';
import { getField, OPERATORS } from '../helpers/fieldCatalog.js';

const selectPortalProps = {
  classNamePrefix: 'rs',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: base => ({ ...base, zIndex: 9999 }) },
};

const ValueInput = ({ fieldKey, op, value, onChange }) => {
  const field = getField(fieldKey);
  // Always call hooks unconditionally — pulling Redux data even when not needed
  // keeps the hook order stable across re-renders.
  const services = useSelector(s => s.providers?.services);
  const providers = useSelector(s => s.providers?.provider);

  if (!field || !op) {
    return <Input disabled placeholder="—" />;
  }
  const arity = OPERATORS[op]?.valueArity;
  if (arity === 'none') return <span className="text-muted small">(no value)</span>;

  // --- Redux-backed select for topService (stores service_id, displays name) ---
  // Mirrors provider-by-id below: the profile stores the immutable service_id,
  // the UI shows the human-readable name.
  if (field.dataSource === 'service-by-id') {
    const opts = Object.values(services || {})
      .filter(s => s && (s.key || s.id) && s.name && s.active !== false)
      .map(s => ({ value: s.key || s.id, label: s.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const labelFor = (id) => {
      const m = opts.find(o => o.value === id);
      return m ? m.label : id;
    };
    if (arity === 'list') {
      const arr = Array.isArray(value) ? value : [];
      return (
        <Select
          isMulti
          value={arr.map(v => ({ value: v, label: labelFor(v) }))}
          onChange={(items) => onChange((items || []).map(o => o.value))}
          options={opts}
          placeholder="Select services…"
          {...selectPortalProps}
        />
      );
    }
    return (
      <Select
        value={value != null ? { value, label: labelFor(value) } : null}
        onChange={(opt) => onChange(opt ? opt.value : null)}
        options={opts}
        placeholder="Select service…"
        isClearable
        {...selectPortalProps}
      />
    );
  }

  if (field.dataSource === 'provider-by-id') {
    const opts = Object.values(providers || {})
      .map(p => ({ value: p.key || p.id, label: p.name || '(unnamed)' }))
      .filter(o => o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
    const labelFor = (id) => {
      const m = opts.find(o => o.value === id);
      return m ? m.label : id;
    };
    if (arity === 'list') {
      const arr = Array.isArray(value) ? value : [];
      return (
        <Select
          isMulti
          value={arr.map(v => ({ value: v, label: labelFor(v) }))}
          onChange={(items) => onChange((items || []).map(o => o.value))}
          options={opts}
          placeholder="Select providers…"
          {...selectPortalProps}
        />
      );
    }
    return (
      <Select
        value={value != null ? { value, label: labelFor(value) } : null}
        onChange={(opt) => onChange(opt ? opt.value : null)}
        options={opts}
        placeholder="Select provider…"
        isClearable
        {...selectPortalProps}
      />
    );
  }

  // Geo special case — same UI for single-point (geo) and multi-point (geoList).
  // The stored value shape is identical: { lat, lng, km }. Only the profile field
  // being matched differs (single geoPoint vs array allGeoPoints).
  if ((field.type === 'geo' || field.type === 'geoList') && op === 'withinRadius') {
    const v = value || { lat: '', lng: '', km: 10 };
    return (
      <div className="d-flex">
        <Input
          type="number" step="0.0001" placeholder="lat" value={v.lat ?? ''}
          onChange={(e) => onChange({ ...v, lat: parseFloat(e.target.value) })}
          className="mr-1"
        />
        <Input
          type="number" step="0.0001" placeholder="lng" value={v.lng ?? ''}
          onChange={(e) => onChange({ ...v, lng: parseFloat(e.target.value) })}
          className="mr-1"
        />
        <Input
          type="number" step="0.1" placeholder="km" value={v.km ?? ''}
          onChange={(e) => onChange({ ...v, km: parseFloat(e.target.value) })}
        />
      </div>
    );
  }

  // Between range — two values
  if (arity === 'two') {
    const [a, b] = Array.isArray(value) ? value : [null, null];
    return (
      <div className="d-flex align-items-center">
        <Input
          type="number" placeholder="min" value={a ?? ''}
          onChange={(e) => onChange([parseFloat(e.target.value), b])}
          className="mr-1"
        />
        <span className="mx-1">and</span>
        <Input
          type="number" placeholder="max" value={b ?? ''}
          onChange={(e) => onChange([a, parseFloat(e.target.value)])}
        />
      </div>
    );
  }

  // Enum field with `in` / `notIn` → multi-select
  if (field.type === 'enum' && arity === 'list') {
    const opts = (field.enumValues || []).map(v => ({ value: v, label: v }));
    const selected = (Array.isArray(value) ? value : []).map(v => ({ value: v, label: v }));
    return (
      <Select
        isMulti
        value={selected}
        onChange={(arr) => onChange((arr || []).map(o => o.value))}
        options={opts}
        classNamePrefix="rs"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  }

  // Enum field with eq/neq → single select
  if (field.type === 'enum') {
    const opts = (field.enumValues || []).map(v => ({ value: v, label: v }));
    const selected = value != null ? { value, label: String(value) } : null;
    return (
      <Select
        value={selected}
        onChange={(opt) => onChange(opt ? opt.value : null)}
        options={opts}
        classNamePrefix="rs"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  }

  // Boolean
  if (field.type === 'boolean') {
    return (
      <Select
        value={value != null ? { value, label: String(value) } : null}
        onChange={(opt) => onChange(opt ? opt.value : null)}
        options={[{ value: true, label: 'true' }, { value: false, label: 'false' }]}
        classNamePrefix="rs"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  }

  // Date as ms — show as datetime-local, store as ms
  if (field.type === 'date') {
    const display = value ? new Date(value).toISOString().slice(0, 16) : '';
    return (
      <Input
        type="datetime-local"
        value={display}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value).getTime() : null)}
      />
    );
  }

  // Number
  if (field.type === 'number') {
    const inner = (
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        placeholder={field.unit ? field.unit : 'number'}
      />
    );
    if (field.unit) {
      return (
        <InputGroup>
          {inner}
          <InputGroupAddon addonType="append"><InputGroupText>{field.unit}</InputGroupText></InputGroupAddon>
        </InputGroup>
      );
    }
    return inner;
  }

  // Array field. Widget depends on arity and whether the array is enum-typed.
  //   contains       → single value  (text input, or enum single-select)
  //   containsAny    → multiple      (multi-select — enum-constrained if enumValues, otherwise creatable)
  //   notContains    → single value
  if (field.type === 'array') {
    if (arity === 'list' && field.enumValues) {
      const opts = field.enumValues.map(v => ({ value: v, label: v }));
      const selected = (Array.isArray(value) ? value : []).map(v => ({ value: v, label: v }));
      return (
        <Select
          isMulti
          value={selected}
          onChange={(arr) => onChange((arr || []).map(o => o.value))}
          options={opts}
          classNamePrefix="rs"
          {...selectPortalProps}
        />
      );
    }
    if (field.enumValues) {
      const opts = field.enumValues.map(v => ({ value: v, label: v }));
      const selected = value != null ? { value, label: String(value) } : null;
      return (
        <Select
          value={selected}
          onChange={(opt) => onChange(opt ? opt.value : null)}
          options={opts}
          classNamePrefix="rs"
          {...selectPortalProps}
        />
      );
    }
    if (arity === 'list') {
      const arr = Array.isArray(value) ? value : [];
      return (
        <Select
          isMulti
          value={arr.map(v => ({ value: v, label: v }))}
          onChange={(items) => onChange((items || []).map(o => o.value))}
          options={[]}
          isClearable
          placeholder="Type and press Enter to add"
          formatCreateLabel={(input) => `Add "${input}"`}
          classNamePrefix="rs"
          {...selectPortalProps}
        />
      );
    }
    return (
      <Input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="value"
      />
    );
  }

  // String: in/notIn → comma-separated multi
  if (field.type === 'string' && arity === 'list') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <Select
        isMulti
        value={arr.map(v => ({ value: v, label: v }))}
        onChange={(items) => onChange((items || []).map(o => o.value))}
        options={[]}
        isClearable
        placeholder="Type and press Enter to add"
        formatCreateLabel={(input) => `Add "${input}"`}
        // react-select v3 doesn't have creatable here; users can paste comma-separated.
        classNamePrefix="rs"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    );
  }

  // Default: text
  return (
    <Input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
    />
  );
};

export default ValueInput;
